import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Shield, CheckCircle, Users, FileCheck, Award } from "lucide-react";

const TransportColisMarocFiable = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Transport de Colis Maroc Fiable – Sécurité et Confiance | GoBledPro</title>
        <meta 
          name="description" 
          content="Découvrez comment GoBledPro garantit un transport de colis France-Maroc fiable : vérification des transporteurs, suivi des envois et engagement qualité." 
        />
        <link rel="canonical" href="https://gobledpro.com/transport-colis-maroc-fiable" />
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <header className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
                Confiance
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                Transport de Colis France – Maroc : Fiabilité et Sécurité
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Pourquoi la fiabilité est essentielle dans le transport de colis et comment GoBledPro 
                s'engage à vous offrir un service de qualité.
              </p>
            </header>

            {/* Introduction */}
            <section className="prose prose-lg max-w-none mb-12">
              <p className="text-muted-foreground leading-relaxed">
                Envoyer un colis entre la France et le Maroc représente souvent bien plus qu'une simple expédition : 
                ce sont des cadeaux pour la famille, des effets personnels précieux, ou des documents importants. 
                La fiabilité du transporteur n'est donc pas une option, c'est une nécessité.
              </p>
            </section>

            {/* Qu'est-ce qu'un transport fiable */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Qu'est-ce qu'un transport fiable ?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Respect des délais</h3>
                  <p className="text-muted-foreground text-sm">
                    Un transporteur fiable communique des délais réalistes et les respecte. 
                    Les retards sont exceptionnels et toujours signalés à l'avance.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Protection des colis</h3>
                  <p className="text-muted-foreground text-sm">
                    Vos colis sont manipulés avec soin, transportés dans des conditions adaptées 
                    et protégés contre les dommages pendant tout le trajet.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <FileCheck className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Transparence</h3>
                  <p className="text-muted-foreground text-sm">
                    Prix clairs sans frais cachés, conditions de transport explicites, 
                    et communication fluide tout au long du processus.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Service client réactif</h3>
                  <p className="text-muted-foreground text-sm">
                    En cas de question ou de problème, un interlocuteur est disponible 
                    pour vous répondre et trouver des solutions.
                  </p>
                </div>
              </div>
            </section>

            {/* Les risques d'un transport non fiable */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Les risques d'un transport non fiable
              </h2>
              <div className="bg-muted/30 rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-6">
                  Faire appel à un transporteur peu fiable peut entraîner des conséquences sérieuses :
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                    <div>
                      <span className="font-medium text-foreground">Perte de colis</span>
                      <p className="text-sm text-muted-foreground mt-1">
                        Sans suivi ni traçabilité, impossible de savoir où se trouve votre colis ou de le récupérer.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                    <div>
                      <span className="font-medium text-foreground">Colis endommagés</span>
                      <p className="text-sm text-muted-foreground mt-1">
                        Une mauvaise manutention ou des conditions de transport inadaptées peuvent abîmer vos biens.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                    <div>
                      <span className="font-medium text-foreground">Retards importants</span>
                      <p className="text-sm text-muted-foreground mt-1">
                        Des semaines d'attente sans nouvelles, des fêtes manquées, des documents arrivés trop tard.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                    <div>
                      <span className="font-medium text-foreground">Aucun recours</span>
                      <p className="text-sm text-muted-foreground mt-1">
                        Sans contrat ni assurance, vous n'avez aucun moyen d'être indemnisé en cas de problème.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            {/* L'engagement GoBledPro */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                L'engagement GoBledPro pour la fiabilité
              </h2>
              <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">Notre processus de vérification</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">1</span>
                    <div>
                      <h4 className="font-medium text-foreground">Vérification d'identité</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Chaque transporteur fournit une pièce d'identité valide que nous vérifions.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">2</span>
                    <div>
                      <h4 className="font-medium text-foreground">Documents professionnels</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Permis de conduire, carte grise du véhicule, et le cas échéant licence de transport.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">3</span>
                    <div>
                      <h4 className="font-medium text-foreground">Assurance vérifiée</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Nous vérifions que le transporteur dispose d'une assurance couvrant les marchandises transportées.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">4</span>
                    <div>
                      <h4 className="font-medium text-foreground">Expérience sur le corridor France-Maroc</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Nous privilégions les transporteurs ayant une expérience avérée sur cette liaison.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Comment reconnaître un transporteur fiable */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Comment reconnaître un transporteur fiable ?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-primary/5 border border-primary/20 rounded-xl">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    Signes positifs
                  </h3>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>• Communication claire et rapide</li>
                    <li>• Prix détaillé sans surprise</li>
                    <li>• Délais précis et réalistes</li>
                    <li>• Possibilité de suivi</li>
                    <li>• Avis clients vérifiables</li>
                    <li>• Assurance mentionnée</li>
                  </ul>
                </div>
                <div className="p-6 bg-destructive/5 border border-destructive/20 rounded-xl">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-destructive" />
                    Signaux d'alerte
                  </h3>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>• Prix anormalement bas</li>
                    <li>• Pas de numéro de téléphone fixe</li>
                    <li>• Réponses vagues sur les délais</li>
                    <li>• Aucune mention d'assurance</li>
                    <li>• Pas de trace en ligne</li>
                    <li>• Demande de paiement intégral à l'avance</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                La fiabilité, notre priorité
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Chez GoBledPro, nous mettons tout en œuvre pour vous connecter avec des transporteurs 
                de confiance. Parce que vos colis méritent d'arriver à bon port.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link 
                  to="/transporteurs-verifies-maroc" 
                  className="text-primary hover:underline font-medium"
                >
                  Découvrir nos transporteurs vérifiés →
                </Link>
                <Link 
                  to="/avis-transport-colis-france-maroc" 
                  className="text-primary hover:underline font-medium"
                >
                  Lire les avis clients →
                </Link>
              </div>
            </section>

            {/* Retour */}
            <div className="pt-8 border-t border-border">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TransportColisMarocFiable;
